import { DeleteOutline, Edit } from '@mui/icons-material';
import { Box, styled } from '@mui/material';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypesManagement from 'hooks/lp/data/accounts/useTransactionTypesManagement';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { type TransactionTypeData } from './TransactionTypeFormContent';

const TableWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  paddingTop: 0,
}));

type TransactionType = TransactionTypeData & {
  runningBalance?: number;
  transactionsCount?: number;
  isForCashTransactions?: boolean;
  isForBankTransferReceivables?: boolean;
  isForCashRevaluationTransactions?: boolean;
  isForPrepaymentTransactions?: boolean;
};

type Props = {
  accountId: string;
  vehicleId: string;
  transactionTypes: Array<TransactionType>;
  onEditTransactionType: (transactionType: TransactionType) => void;
};

const TransactionTypesTable: React.FC<Props> = ({
  vehicleId,
  transactionTypes,
  onEditTransactionType,
}) => {
  const { deleteTransactionType } = useTransactionTypesManagement();
  const [canEdit, canDelete] = useHasPermissions([
    RolePermission.LpEditAccount,
    RolePermission.LpDeleteAccount,
  ]);

  return (
    <TableWrapper>
      <Table
        columns={[
          {
            key: 'name',
            label: 'Transaction name',
            render: (row) => row.name,
          },
          {
            key: 'runningBalance',
            label: 'Running balance',
            render: (row) => numberToCurrencyString(row.runningBalance),
          },
        ]}
        rows={transactionTypes ?? []}
        disableSorting
        actions={[
          {
            label: 'Edit',
            icon: Edit,
            hidden: !canEdit,
            onClick: (row) => onEditTransactionType(row),
          },
          {
            label: 'Delete',
            icon: DeleteOutline,
            hidden: !canDelete,
            disabled: (row) =>
              !!row.transactionsCount ||
              !!row.isForCashTransactions ||
              !!row.isForCashRevaluationTransactions ||
              !!row.isForPrepaymentTransactions ||
              !!row.isForBankTransferReceivables,
            tooltip: (row) =>
              row.transactionsCount
                ? 'This transaction type cannot be deleted while it has linked transactions. Please see the General Ledger to review all linked transactions.'
                : undefined,
            onClick: (row) =>
              deleteTransactionType.mutate({
                transactionTypeId: row.id,
                vehicleId,
              }),
          },
        ]}
      />
    </TableWrapper>
  );
};

export default TransactionTypesTable;
